import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { DEFAULT_BANK, Question, Quiz, QuizBank, bankKey } from '../models/quiz';

export const DEFAULT_QUIZ_LANGUAGE = 'am';
const DATA_URL = 'assets/data/quizzes';
const IMAGE_PATH_PREFIX = 'assets/images/quiz';

export interface QuizSet {
  quizzes: Quiz[];
  /** Language the questions are actually in. */
  language: string;
  /** True when the requested language has no questions yet and Armenian is shown instead. */
  isFallback: boolean;
}

@Injectable({ providedIn: 'root' })
export class QuizDataService {
  private cache = new Map<string, Observable<Quiz[] | null>>();

  constructor(private http: HttpClient) {}

  getQuizSet(language: string, bank: QuizBank = DEFAULT_BANK): Observable<QuizSet> {
    const requested = language || DEFAULT_QUIZ_LANGUAGE;
    return this.load(requested, bank).pipe(
      switchMap((quizzes) => {
        if (quizzes || requested === DEFAULT_QUIZ_LANGUAGE) {
          return of({ quizzes: quizzes ?? [], language: requested, isFallback: false });
        }
        return this.load(DEFAULT_QUIZ_LANGUAGE, bank).pipe(
          map((fallback) => ({ quizzes: fallback ?? [], language: DEFAULT_QUIZ_LANGUAGE, isFallback: true }))
        );
      })
    );
  }

  getQuiz(language: string, id: number, bank: QuizBank = DEFAULT_BANK): Observable<{ quiz: Quiz | undefined; set: QuizSet }> {
    return this.getQuizSet(language, bank).pipe(
      map((set) => ({ quiz: set.quizzes.find((q) => q.id === id), set }))
    );
  }

  private load(language: string, bank: QuizBank): Observable<Quiz[] | null> {
    // 2026 edition: quizzes/v2/<category>/<lang>.json.
    const file = `v2/${bank.category}/${language}.json`;
    const cacheKey = `${bankKey(bank)}/${language}`;
    let request = this.cache.get(cacheKey);
    if (!request) {
      request = this.http.get<Quiz[]>(`${DATA_URL}/${file}`).pipe(
        map((quizzes) => (Array.isArray(quizzes) && quizzes.length ? quizzes.map(normalizeQuiz) : null)),
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.cache.set(cacheKey, request);
    }
    return request;
  }
}

function normalizeQuiz(quiz: Quiz): Quiz {
  return {
    ...quiz,
    questions: quiz.questions.map(
      (question): Question => ({
        ...question,
        options: [...question.options],
        imagePath: question.imagePath ? `${IMAGE_PATH_PREFIX}${question.imagePath}` : null,
      })
    ),
  };
}
